import React, { useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import type { RootState } from "@redux/store/store";
import {
	setVrfFilterSelection,
	setConnectorTypeFilterSelection,
	setBgpStatusFilterSelection,
} from "@redux/slices/kontrolEdge/backboneDetailsSlice";
import {
	BACKBONE_BGP_STATUS_FILTER_DOWN,
	BACKBONE_BGP_STATUS_FILTER_UP,
} from "./BackboneDetailsData";
import MultiFilterDropdown, {
	type MultiFilterFieldConfig,
	type MultiFilterValue,
} from "@ui-components/Filter/MultiFilterDropdown";

const VRF_FIELD_ID = "vrf";
const CONNECTOR_TYPE_FIELD_ID = "connectorType";
const BGP_STATUS_FIELD_ID = "bgpStatus";

/**
 * Backbone-detail-scoped filter UI. Adapter that maps the generic
 * {@link MultiFilterDropdown} value to the backboneDetails slice (Redux).
 *
 * Sessions persistence (Redux) is intentional; the slice resets when the
 * backbone detail unmounts via `clearBackboneVrfs`.
 */
export default function BackboneFilters() {
	const dispatch = useAppDispatch();
	const {
		vrfs,
		vrfCounts,
		vrfFilterSelection,
		connectorTypes,
		connectorTypeCounts,
		connectorTypeFilterSelection,
		bgpStatusFilterSelection,
		bgpStatusCounts,
	} = useAppSelector((state: RootState) => state.backboneDetails);

	const bgpStatusTotal =
		(bgpStatusCounts.up ?? 0) + (bgpStatusCounts.down ?? 0) > 0;

	const fields: MultiFilterFieldConfig[] = useMemo(() => {
		const out: MultiFilterFieldConfig[] = [];
		out.push({
			id: VRF_FIELD_ID,
			label: "VRF",
			options: vrfs.map((v) => ({ id: v, label: v, count: vrfCounts[v] })),
			searchable: true,
			searchPlaceholder: "Search VRFs…",
			emptyOptionsMessage: "No matching VRFs",
		});
		if (connectorTypes.length > 0) {
			out.push({
				id: CONNECTOR_TYPE_FIELD_ID,
				label: "Connector",
				options: connectorTypes.map((t) => ({
					id: t,
					label: t,
					count: connectorTypeCounts[t],
				})),
				searchable: false,
				emptyOptionsMessage: "No connector types",
			});
		}
		if (bgpStatusTotal) {
			out.push({
				id: BGP_STATUS_FIELD_ID,
				label: "BGP status",
				options: [
					{
						id: BACKBONE_BGP_STATUS_FILTER_UP,
						label: "Up",
						count: bgpStatusCounts.up ?? 0,
					},
					{
						id: BACKBONE_BGP_STATUS_FILTER_DOWN,
						label: "Down",
						count: bgpStatusCounts.down ?? 0,
					},
				],
				searchable: false,
				emptyOptionsMessage: "No BGP status options",
			});
		}
		return out;
	}, [
		vrfs,
		vrfCounts,
		connectorTypes,
		connectorTypeCounts,
		bgpStatusTotal,
		bgpStatusCounts.up,
		bgpStatusCounts.down,
	]);

	const value: MultiFilterValue = useMemo(
		() => ({
			[VRF_FIELD_ID]: vrfFilterSelection,
			[CONNECTOR_TYPE_FIELD_ID]: connectorTypeFilterSelection,
			[BGP_STATUS_FIELD_ID]: bgpStatusFilterSelection,
		}),
		[vrfFilterSelection, connectorTypeFilterSelection, bgpStatusFilterSelection]
	);

	const handleApply = (next: MultiFilterValue) => {
		const nextVrf = next[VRF_FIELD_ID] ?? [];
		const nextType = next[CONNECTOR_TYPE_FIELD_ID] ?? [];
		const nextBgp = next[BGP_STATUS_FIELD_ID] ?? [];
		dispatch(setVrfFilterSelection(nextVrf));
		dispatch(setConnectorTypeFilterSelection(nextType));
		dispatch(setBgpStatusFilterSelection(nextBgp));
	};

	return (
		<div className="mt-3 pt-5 mb-3 w-full border-t border-semantic-border-primary">
			<MultiFilterDropdown
				fields={fields}
				value={value}
				onApply={handleApply}
				triggerLabel="Filters"
				cardTitle="Filters"
			/>
		</div>
	);
}
